/**
 * Utility functions for personal note date handling.
 * Generates date strings, display titles, and date ranges for daily/weekly notes.
 *
 * Timezone model:
 * - "Now"-dependent functions (getTodayDateString, getCurrentWeekString,
 *   isToday, isThisWeek, generateDateRange, generateWeekRange) accept an
 *   optional IANA timezone (from the user's preference).
 * - Invalid / unknown zones fall back to the browser-local zone, then UTC.
 * - Week keys are always ISO 8601 (`YYYY-Wnn`, Monday-start). The user's
 *   `WeekStartsOn` preference intentionally does not affect weekly-note keys
 *   because the stored key format would need a data migration.
 */
import { DateTime } from 'luxon'

/**
 * Resolve an IANA zone name. Falls back to browser-local, then UTC, if the
 * input zone is missing or not understood by Luxon. Backend `TimeZone` is
 * only length-validated, so bad values can reach this code at runtime.
 */
function resolveZone(timezone?: string): string {
  const localZone = DateTime.local().zoneName ?? 'UTC'
  if (!timezone) return localZone
  return DateTime.now().setZone(timezone).isValid ? timezone : localZone
}

function nowIn(timezone?: string): DateTime {
  return DateTime.now().setZone(resolveZone(timezone))
}

/**
 * Get today's date as ISO string "YYYY-MM-DD" in the given timezone.
 */
export function getTodayDateString(timezone?: string): string {
  return nowIn(timezone).toFormat('yyyy-LL-dd')
}

/**
 * Get the current ISO week string "YYYY-Wnn" in the given timezone.
 */
export function getCurrentWeekString(timezone?: string): string {
  const dt = nowIn(timezone)
  return `${dt.weekYear}-W${String(dt.weekNumber).padStart(2, '0')}`
}

/**
 * Format a Date to "YYYY-MM-DD" using its local components. Used for
 * formatting already-materialised Date instances; not "now"-dependent.
 */
export function formatDateToISO(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/**
 * Format a daily note date string for display.
 * "2026-03-29" → "Sun, Mar 29, 2026" (format depends on locale)
 */
export function formatDailyNoteTitle(dateStr: string, locale?: string): string {
  const date = parseDateString(dateStr)
  if (!date) return dateStr
  return date.toLocaleDateString(locale, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

/**
 * Format a weekly note date string for display.
 * "2026-W13" → "Week 13, 2026"
 *
 * Note: the "Week" literal is not localised yet; callers that need a
 * translated label should read it from the `notes` i18n namespace.
 */
export function formatWeeklyNoteTitle(weekStr: string): string {
  const match = weekStr.match(/^(\d{4})-W(\d{1,2})$/)
  if (!match) return weekStr
  // Capture groups read as `string | undefined`; TS cannot tie them to the
  // match succeeding. Falling back to the raw input is the same behaviour as
  // a failed match.
  const [, year, week] = match
  if (!year || !week) return weekStr
  return `Week ${parseInt(week, 10)}, ${year}`
}

/**
 * Check if a date string is today in the given timezone.
 */
export function isToday(dateStr: string, timezone?: string): boolean {
  return dateStr === getTodayDateString(timezone)
}

/**
 * Check if a week string is the current ISO week in the given timezone.
 */
export function isThisWeek(weekStr: string, timezone?: string): boolean {
  return weekStr === getCurrentWeekString(timezone)
}

/**
 * Parse "YYYY-MM-DD" to a Date object (local time at midnight).
 */
export function parseDateString(dateStr: string): Date | null {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/)
  if (!match) return null
  const [, year, month, day] = match
  if (!year || !month || !day) return null
  const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
  return isNaN(date.getTime()) ? null : date
}

/**
 * Generate an array of date strings going back from today in the given
 * timezone. Returns dates in descending order (newest first).
 */
export function generateDateRange(count: number, timezone?: string): Array<string> {
  const today = nowIn(timezone)
  const dates: Array<string> = []
  for (let i = 0; i < count; i++) {
    dates.push(today.minus({ days: i }).toFormat('yyyy-LL-dd'))
  }
  return dates
}

/**
 * Generate an array of ISO week strings going back from the current week in
 * the given timezone. Returns weeks in descending order (newest first).
 */
export function generateWeekRange(count: number, timezone?: string): Array<string> {
  const now = nowIn(timezone)
  const weeks: Array<string> = []
  for (let i = 0; i < count; i++) {
    const dt = now.minus({ weeks: i })
    weeks.push(`${dt.weekYear}-W${String(dt.weekNumber).padStart(2, '0')}`)
  }
  // Deduplicate (in case multiple weeks map to same key across year boundaries)
  return [...new Set(weeks)].slice(0, count)
}

/**
 * Get the start date (Monday) of an ISO week.
 * Used to display the week's date range.
 */
export function getWeekStartDate(weekStr: string): Date | null {
  const match = weekStr.match(/^(\d{4})-W(\d{1,2})$/)
  if (!match) return null
  const [, yearStr, weekStrPart] = match
  if (!yearStr || !weekStrPart) return null
  const year = parseInt(yearStr)
  const week = parseInt(weekStrPart)

  const dt = DateTime.fromObject({ weekYear: year, weekNumber: week, weekday: 1 })
  return dt.isValid ? dt.toJSDate() : null
}

/**
 * The daily or weekly note key `delta` periods away, for prev / next
 * navigation. "2026-09-27" +1 → "2026-09-28"; "2026-W39" -1 → "2026-W38".
 */
export function shiftNoteDate(
  noteType: 'daily' | 'weekly',
  noteDate: string,
  delta: number,
): string {
  if (noteType === 'daily') {
    const date = parseDateString(noteDate)
    if (!date) return noteDate
    return DateTime.fromJSDate(date).plus({ days: delta }).toFormat('yyyy-LL-dd')
  }
  const start = getWeekStartDate(noteDate)
  if (!start) return noteDate
  const dt = DateTime.fromJSDate(start).plus({ weeks: delta })
  return `${dt.weekYear}-W${String(dt.weekNumber).padStart(2, '0')}`
}
